import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Save, X, AlertCircle, Check, Loader2 } from 'lucide-react'
import { z } from 'zod'

// Zod schema for form validation
export const contentMetadataSchema = z.object({
  fileId: z.string().min(1, 'File ID is required'),
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  accessLevel: z.enum(['demo', 'company', 'admin']),
  type: z.enum(['presentation', 'pdf', 'image', 'video', 'markdown']),
  filePath: z.string().min(1, 'File path is required'),
  thumbnailUrl: z.string().optional().nullable(),
  companyId: z.string().uuid().optional().nullable(),
  tags: z.array(z.string()).optional()
})

export type ContentMetadataFormData = z.infer<typeof contentMetadataSchema>

interface ContentMetadataFormProps {
  fileId: string
  fileType: string
  filePath: string
  thumbnailUrl?: string | null
  initialData?: Partial<ContentMetadataFormData>
  onSave?: (data: ContentMetadataFormData) => void
  onCancel?: () => void
}

export function ContentMetadataForm({
  fileId,
  fileType,
  filePath,
  thumbnailUrl,
  initialData,
  onSave,
  onCancel
}: ContentMetadataFormProps) {
  const [formData, setFormData] = useState<ContentMetadataFormData>({
    fileId,
    title: initialData?.title || '',
    description: initialData?.description || '',
    accessLevel: initialData?.accessLevel || 'company',
    type: fileType as any,
    filePath,
    thumbnailUrl: thumbnailUrl || null,
    companyId: initialData?.companyId || null,
    tags: initialData?.tags || []
  })

  const [tagInput, setTagInput] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null
  const isSuperAdmin = user?.role === 'super_admin'

  const validateField = (field: keyof ContentMetadataFormData, value: any) => {
    try {
      const partialSchema = contentMetadataSchema.pick({ [field]: true as never })
      partialSchema.parse({ [field]: value })
      setErrors(prev => ({ ...prev, [field]: '' }))
      return true
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors(prev => ({
          ...prev,
          [field]: error.issues[0]?.message || 'Invalid value'
        }))
      }
      return false
    }
  }

  const handleInputChange = (
    field: keyof ContentMetadataFormData,
    value: string | string[] | null
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    validateField(field, value)
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }))
      setTagInput('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }))
  }

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    try {
      contentMetadataSchema.parse(formData)
      setErrors({})
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {}
        error.issues.forEach((err: any) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message
          }
        })
        setErrors(newErrors)
        return
      }
    }

    setSaving(true)
    setSaveStatus({ type: null, message: '' })

    try {
      const accessToken = localStorage.getItem('token')

      // Add companyId from user if not super admin
      const payload = isSuperAdmin
        ? formData
        : { ...formData, companyId: user?.companyId || null }

      const response = await fetch('http://localhost:3000/api/content/metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save metadata')
      }

      setSaveStatus({
        type: 'success',
        message: 'Content metadata saved successfully!'
      })

      setTimeout(() => {
        setSaveStatus({ type: null, message: '' })
        onSave?.(formData)
      }, 2000)

    } catch (error: any) {
      setSaveStatus({
        type: 'error',
        message: error.message || 'Failed to save metadata'
      })

      setTimeout(() => {
        setSaveStatus({ type: null, message: '' })
      }, 5000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto"
    >
      <div className="bg-card rounded-lg border border-border p-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Content Metadata</h2>
          <p className="text-muted-foreground">
            Add metadata to organize and categorize your content
          </p>
        </div>

        {/* Status Messages */}
        <AnimatePresence>
          {saveStatus.type && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                saveStatus.type === 'success'
                  ? 'bg-green-500/10 text-green-600 border border-green-500/20'
                  : 'bg-red-500/10 text-red-600 border border-red-500/20'
              }`}
            >
              {saveStatus.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p>{saveStatus.message}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title (Required) */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border bg-background ${
                errors.title ? 'border-destructive' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-primary`}
              placeholder="Enter content title"
              disabled={saving}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 rounded-lg border bg-background ${
                errors.description ? 'border-destructive' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
              placeholder="Enter a description for this content"
              disabled={saving}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
            <p className="mt-1 text-sm text-muted-foreground">
              {formData.description?.length || 0} / 2000 characters
            </p>
          </div>

          {/* Access Level */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Access Level
            </label>
            <select
              value={formData.accessLevel}
              onChange={(e) => handleInputChange('accessLevel', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border bg-background ${
                errors.accessLevel ? 'border-destructive' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-primary`}
              disabled={saving}
            >
              <option value="demo">Demo - Public trial content</option>
              <option value="company">Company - Company members only</option>
              <option value="admin">Admin - Admins only</option>
            </select>
            {errors.accessLevel && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.accessLevel}
              </p>
            )}
          </div>

          {/* Company ID (Super Admin only) */}
          {isSuperAdmin && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Company ID
              </label>
              <input
                type="text"
                value={formData.companyId || ''}
                onChange={(e) => handleInputChange('companyId', e.target.value || null)}
                className={`w-full px-4 py-2 rounded-lg border bg-background ${
                  errors.companyId ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                placeholder="Leave empty for platform-wide content"
                disabled={saving}
              />
              {errors.companyId && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.companyId}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                Leave empty to make this content available to all companies
              </p>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Tags
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                className="flex-1 px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add a tag and press Enter"
                disabled={saving}
              />
              <motion.button
                type="button"
                onClick={handleAddTag}
                disabled={saving || !tagInput.trim()}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: saving ? 1 : 1.02 }}
                whileTap={{ scale: saving ? 1 : 0.98 }}
              >
                Add
              </motion.button>
            </div>

            {/* Tags List */}
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                      disabled={saving}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </motion.span>
                ))}
              </div>
            )}
          </div>

          {/* File Info (Read-only) */}
          <div className="p-4 bg-secondary rounded-lg">
            <h3 className="font-medium mb-2">File Information</h3>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p><span className="font-medium">Type:</span> {formData.type}</p>
              <p><span className="font-medium">File Path:</span> {formData.filePath}</p>
              {formData.thumbnailUrl && (
                <p><span className="font-medium">Thumbnail:</span> Available</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <motion.button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: saving ? 1 : 1.02 }}
              whileTap={{ scale: saving ? 1 : 0.98 }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Metadata
                </>
              )}
            </motion.button>

            {onCancel && (
              <motion.button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: saving ? 1 : 1.02 }}
                whileTap={{ scale: saving ? 1 : 0.98 }}
              >
                Cancel
              </motion.button>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  )
}
